import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useR05Api from '../api/useR05Api';
import { CacheManager } from '../utils/CacheManager/CacheManager';
import { TIMEOUT_DURATION } from '../utils/Constants';
import { showNotificationDialog } from '../utils';
import { DistributionQrCode, ErrorType } from 'types';

const useQrCode = () => {
    const [isLoading, setLoading] = useState(false);
    const [qrCode, setQrCode] = useState<string>();
    const [isQrCodeUsed, setIsQrCodeUsed] = useState(false);
    const [distributionQrCode, setDistributionQrCode] = useState<DistributionQrCode>();
    const { t } = useTranslation();
    const { fetchQrCode: getQrCode, fetchDistributionQrCode: getDistributionQrCode, receiveTicket } = useR05Api();
    const navigate = useNavigate();

    /**
     * fetch errorの対応
     */
    const showFetchError = useCallback(
        (error: ErrorType) => {
            const showError = (text: string) => {
                showNotificationDialog('error', 'fetchQrCode', t('notification.title'), text);
            };

            switch (error.errorType) {
                case 'remaining_order_distribution_status_is_not_bought':
                case 'order_distribution_is_used':
                case 'order_status_is_not_bought':
                case 'ticket_used':
                    setIsQrCodeUsed(true);
                    break;
                case 'ticket_already_used':
                    showError(t('notification.ticket-used'));
                    break;
                case 'offline':
                case 'timeout':
                default:
                    showError(t('notification.network-failed'));
                    break;
            }
        },
        [t],
    );

    /**
     * QRコードを取得する。（オンライン/低速/オフライン)
     */
    const fetchQrCode = useCallback(
        async (requestParams: {
            remainingOrderDistributionId?: string | null;
            orderDistributionId?: string | null;
            orderTicketId?: string | null;
        }) => {
            setLoading(true);

            const cacheName = 'qr_code_cache';
            const cacheUrl = `/qr_code?referenceId=${
                requestParams.remainingOrderDistributionId ??
                requestParams.orderTicketId ??
                requestParams.orderDistributionId
            }`;
            const qrCodeCache = new CacheManager(cacheName, cacheUrl);

            // オンライン時のfetch
            const fetchOnline = async () => {
                const { qrCode } = (await getQrCode(requestParams, TIMEOUT_DURATION)) ?? {};
                await qrCodeCache.saveToCache(qrCode);
                setQrCode(qrCode);
            };

            // オフライン or 低速時のfetch
            const fetchOffline = async () => {
                const response = await qrCodeCache.readFromCache();
                setQrCode(response);
            };

            // キャッシュのデータをまず表示、エラー時には何もしない
            try {
                await fetchOffline();
            } catch {}
            setLoading(false);

            // オンライン時は、ポップアップのローディングを出して、チケット一覧を更新する
            if (navigator.onLine) {
                setLoading(true);
                try {
                    await fetchOnline();
                } catch (error) {
                    showFetchError(error as ErrorType);
                }
                setLoading(false);
            }
        },
        [],
    );

    /**
     * 分配QRコードを取得する
     */
    const fetchDistributionQrCode = useCallback(async (requestParams: { distributionCode: string }) => {
        try {
            setLoading(true);
            const response = await getDistributionQrCode(requestParams, TIMEOUT_DURATION);
            const data = {
                ...response,
                ...response?.orderTicket,
                ...response?.orderTicket?.order,
                ...response?.orderTicket?.orderUsageTickets?.[0]?.reservationInfo,
            };
            setDistributionQrCode(data);
            setLoading(false);
            return data;
        } catch (error) {
            showFetchError(error as ErrorType);
            setLoading(false);
        }
    }, []);

    /**
     * チケット受け取り
     */
    const receiveDistributionTicket = useCallback(
        async (requestParams: { orderDistributionId?: string; distributionCode?: string }) => {
            try {
                setLoading(true);
                const response = await receiveTicket(requestParams, TIMEOUT_DURATION);
                setLoading(false);
                return response.message;
            } catch (error) {
                showFetchError(error as ErrorType);
                setLoading(false);
            }
        },
        [],
    );

    const closeConfirmQrCodeModal = (redirectUrl: string) => {
        setIsQrCodeUsed(false);
        navigate(redirectUrl || '/bought');
    };

    return {
        fetchQrCode,
        isLoading,
        qrCode,
        isOpenConfirmQrCodeModal: isQrCodeUsed,
        closeConfirmQrCodeModal,
        fetchDistributionQrCode,
        distributionQrCode,
        receiveDistributionTicket,
    };
};

export default useQrCode;
